import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import api from '@/api';
import { toast } from 'sonner';
import { Star, Loader2, Trash2, Edit2, Check, X, ExternalLink, Shield, RefreshCw } from 'lucide-react';

const PUB_API = process.env.REACT_APP_BACKEND_URL || '';

// ── Componente stelle ─────────────────────────────────────────────────────────
function Stars({ value, max = 5, size = 14, interactive = false, onChange }) {
  const [hover, setHover] = useState(0);
  return (
    <div className="flex items-center gap-0.5">
      {Array.from({ length: max }, (_, i) => {
        const filled = interactive ? (hover || value) > i : value > i;
        return (
          <Star
            key={i}
            size={size}
            fill={filled ? '#c9a84c' : 'none'}
            stroke={filled ? '#c9a84c' : 'rgba(229,229,229,0.25)'}
            className={interactive ? 'cursor-pointer transition-transform hover:scale-110' : ''}
            onMouseEnter={() => interactive && setHover(i + 1)}
            onMouseLeave={() => interactive && setHover(0)}
            onClick={() => interactive && onChange && onChange(i + 1)}
          />
        );
      })}
    </div>
  );
}

// ── Form review inline ────────────────────────────────────────────────────────
function ReviewForm({ integration, existing, onSaved, onCancel }) {
  const [rating, setRating] = useState(existing?.rating || 0);
  const [comment, setComment] = useState(existing?.comment || '');
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!rating) { toast.error('Seleziona un voto da 1 a 5 stelle'); return; }
    setSaving(true);
    try {
      await api.post(`/marketplace/${integration.integration_id}/review`, { rating, comment });
      toast.success(existing ? 'Recensione aggiornata' : 'Recensione pubblicata');
      onSaved({ integration_id: integration.integration_id, rating, comment });
    } catch { toast.error('Errore salvataggio'); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-3 p-4 rounded-lg" style={{ background: 'var(--lux-surface-3)', border: '1px solid var(--lux-border-2)' }}>
      <div className="flex items-center justify-between">
        <p className="text-sm font-medium text-white">{existing ? 'Modifica recensione' : 'Lascia una recensione'}</p>
        {onCancel && <button onClick={onCancel} className="text-gray-500 hover:text-white transition-colors"><X size={15} /></button>}
      </div>
      <div>
        <p className="text-xs mb-2" style={{ color: 'var(--lux-text-muted-3)' }}>Il tuo voto *</p>
        <Stars value={rating} interactive onChange={setRating} size={22} />
      </div>
      <div>
        <p className="text-xs mb-1.5" style={{ color: 'var(--lux-text-muted-3)' }}>Commento (opzionale, max 500 caratteri)</p>
        <textarea
          value={comment}
          onChange={e => setComment(e.target.value.slice(0, 500))}
          rows={3}
          placeholder="Descrivi la tua esperienza con questa integrazione..."
          data-testid={`review-comment-${integration.integration_id}`}
          className="input-dark w-full text-sm resize-none"
        />
        <p className="text-[10px] text-right mt-0.5" style={{ color: 'var(--lux-text-muted-3)' }}>{comment.length}/500</p>
      </div>
      <div className="flex gap-2">
        <button
          onClick={save}
          disabled={saving || !rating}
          data-testid={`save-review-${integration.integration_id}`}
          className="btn-gold flex items-center gap-2 text-sm"
          style={{ opacity: !rating ? 0.5 : 1 }}
        >
          {saving ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />}
          {existing ? 'Aggiorna' : 'Pubblica'}
        </button>
        {onCancel && (
          <button onClick={onCancel} className="px-3 py-1.5 text-sm rounded-sm" style={{ border: '1px solid var(--lux-border-2)', color: 'var(--lux-text-muted)' }}>
            Annulla
          </button>
        )}
      </div>
    </div>
  );
}

// ── Card singola integrazione ─────────────────────────────────────────────────
function IntegrationReviewCard({ item, myReview, onReviewChange }) {
  const [editing, setEditing] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const handleDelete = async () => {
    if (!window.confirm('Eliminare la tua recensione?')) return;
    setDeleting(true);
    try {
      await api.delete(`/marketplace/${item.integration_id}/review`);
      toast.success('Recensione eliminata');
      onReviewChange(item.integration_id, null);
    } catch { toast.error('Errore eliminazione'); }
    finally { setDeleting(false); }
  };

  const handleSaved = (updated) => {
    onReviewChange(item.integration_id, updated);
    setEditing(false);
  };

  return (
    <div className="card-dark rounded-lg overflow-hidden">
      {/* Header integrazione */}
      <div className="flex items-start gap-3 p-4" style={{ borderBottom: '1px solid var(--lux-border-1)' }}>
        <div className="w-9 h-9 rounded-sm flex items-center justify-center font-bold text-white text-sm flex-shrink-0"
          style={{ background: item.logo_color }}>
          {item.logo_letter}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="font-medium text-sm text-white">{item.name}</p>
            {item.certified && (
              <span className="flex items-center gap-1 px-1.5 py-0.5 text-[9px] rounded" style={{ background: 'rgba(201,168,76,0.12)', color: '#c9a84c', border: '1px solid rgba(201,168,76,0.2)' }}>
                <Shield size={8} /> Certificato
              </span>
            )}
          </div>
          <p className="text-xs mt-0.5" style={{ color: 'var(--lux-text-muted-3)' }}>{item.tagline}</p>
          {/* Rating globale */}
          {item.review_count > 0 && (
            <div className="flex items-center gap-2 mt-1.5">
              <Stars value={item.avg_rating} size={11} />
              <span className="text-[10px]" style={{ color: 'var(--lux-text-muted-3)' }}>
                {item.avg_rating} · {item.review_count} {item.review_count === 1 ? 'recensione' : 'recensioni'}
              </span>
            </div>
          )}
        </div>
        {item.website && (
          <a href={item.website} target="_blank" rel="noreferrer" title="Visita sito"
            className="shrink-0 text-gray-600 hover:text-white transition-colors">
            <ExternalLink size={14} />
          </a>
        )}
      </div>

      {/* Sezione recensione */}
      <div className="p-4">
        {myReview && !editing ? (
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Stars value={myReview.rating} size={14} />
                <span className="text-xs font-medium" style={{ color: '#c9a84c' }}>La tua recensione</span>
              </div>
              <div className="flex gap-2">
                <button onClick={() => setEditing(true)} title="Modifica"
                  data-testid={`edit-review-${item.integration_id}`}
                  className="text-gray-500 hover:text-white transition-colors"><Edit2 size={13} /></button>
                <button onClick={handleDelete} disabled={deleting} title="Elimina"
                  data-testid={`delete-review-${item.integration_id}`}
                  className="text-gray-500 hover:text-red-400 transition-colors">
                  {deleting ? <Loader2 size={13} className="animate-spin" /> : <Trash2 size={13} />}
                </button>
              </div>
            </div>
            {myReview.comment && (
              <p className="text-sm italic" style={{ color: 'var(--lux-text-sec)', lineHeight: 1.6 }}>
                "{myReview.comment}"
              </p>
            )}
            {/* Risposta del team Shangrilux */}
            {myReview.admin_reply && (
              <div className="flex gap-2 p-3 rounded-sm mt-1" style={{ background: 'rgba(201,168,76,0.06)', border: '1px solid rgba(201,168,76,0.15)' }}>
                <div className="w-5 h-5 rounded flex items-center justify-center text-[9px] font-bold flex-shrink-0" style={{ background: '#c9a84c', color: '#000' }}>O</div>
                <div>
                  <p className="text-[10px] font-semibold mb-0.5" style={{ color: '#c9a84c' }}>Team Shangrilux</p>
                  <p className="text-xs" style={{ color: 'var(--lux-text-muted)', lineHeight: 1.5 }}>{myReview.admin_reply}</p>
                </div>
              </div>
            )}
          </div>
        ) : editing ? (
          <ReviewForm
            integration={item}
            existing={myReview}
            onSaved={handleSaved}
            onCancel={() => setEditing(false)}
          />
        ) : (
          <ReviewForm integration={item} onSaved={(r) => onReviewChange(item.integration_id, r)} />
        )}
      </div>
    </div>
  );
}

// ── Pagina principale ─────────────────────────────────────────────────────────
export function MarketplacePanel() {
  const [integrations, setIntegrations] = useState([]);
  const [myReviews, setMyReviews] = useState({});   // integration_id → review
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');       // all | reviewed | unreviewed

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [mktRes, revRes] = await Promise.all([
        axios.get(`${PUB_API}/api/marketplace`),
        api.get('/marketplace/my-reviews/all'),
      ]);
      setIntegrations(mktRes.data.integrations || []);
      const map = {};
      for (const r of revRes.data.reviews || []) map[r.integration_id] = r;
      setMyReviews(map);
    } catch { toast.error('Errore caricamento'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleReviewChange = (integrationId, review) => {
    setMyReviews(prev => {
      if (review === null) {
        const next = { ...prev };
        delete next[integrationId];
        return next;
      }
      return { ...prev, [integrationId]: review };
    });
  };

  const filtered = integrations.filter(item => {
    if (filter === 'reviewed')   return !!myReviews[item.integration_id];
    if (filter === 'unreviewed') return !myReviews[item.integration_id];
    return true;
  });

  const reviewedCount = Object.keys(myReviews).length;

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <Loader2 size={28} className="animate-spin text-[#c9a84c]" />
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-3xl">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="label-gold">Marketplace</p>
          <h2 className="font-serif text-3xl text-white font-light mt-1">Recensioni Integrazioni</h2>
          <p className="text-sm mt-1" style={{ color: 'var(--lux-text-muted)' }}>
            Valuta le integrazioni che hai usato. Le tue recensioni aiutano altri hotel a scegliere gli strumenti migliori.
          </p>
        </div>
        <button onClick={load} className="btn-ghost p-2 shrink-0" title="Ricarica" data-testid="reload-marketplace-btn">
          <RefreshCw size={15} />
        </button>
      </div>

      {/* Stats + filtri */}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-4 text-sm">
          <span style={{ color: 'var(--lux-text-muted)' }}>
            <span className="text-white font-medium">{reviewedCount}</span> / {integrations.length} recensite
          </span>
          <div className="w-32 h-1 rounded-full overflow-hidden" style={{ background: 'var(--lux-surface-3)' }}>
            <div className="h-full rounded-full transition-all" style={{ width: `${integrations.length ? (reviewedCount / integrations.length) * 100 : 0}%`, background: '#c9a84c' }} />
          </div>
        </div>
        <div className="flex gap-1.5">
          {[
            { id: 'all',        label: 'Tutte' },
            { id: 'reviewed',   label: 'Recensite' },
            { id: 'unreviewed', label: 'Da valutare' },
          ].map(f => (
            <button key={f.id} onClick={() => setFilter(f.id)}
              data-testid={`filter-reviews-${f.id}`}
              className="px-3 py-1 text-xs rounded-sm transition-all"
              style={{
                background: filter === f.id ? '#c9a84c' : 'var(--lux-surface-2)',
                color: filter === f.id ? '#000' : 'var(--lux-text-muted)',
                border: `1px solid ${filter === f.id ? '#c9a84c' : 'var(--lux-border-2)'}`,
                fontWeight: filter === f.id ? 600 : 400,
              }}>
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {/* Lista */}
      {filtered.length === 0 ? (
        <div className="text-center py-16 rounded-lg" style={{ border: '1px dashed var(--lux-border-2)' }}>
          <p style={{ color: 'var(--lux-text-muted-3)' }}>
            {filter === 'reviewed' ? 'Non hai ancora lasciato recensioni.' : 'Tutte le integrazioni sono state recensite.'}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4">
          {filtered.map(item => (
            <IntegrationReviewCard
              key={item.integration_id}
              item={item}
              myReview={myReviews[item.integration_id] || null}
              onReviewChange={handleReviewChange}
            />
          ))}
        </div>
      )}
    </div>
  );
}
